import type { UserInfo } from "Infrastructure/Api/Api";
import {
  CODE_VERIFIER_KEY,
  JWT_TOKEN_KEY,
  USER_DATA_KEY,
} from "Infrastructure/Constants/AuthConstants";

import {
  backendAuthenticate,
  getAuthUrl,
} from "Infrastructure/Services/AuthService";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

type AuthContextType = {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const login = async () => {
    const authUrl = await getAuthUrl();
    window.location.href = authUrl;
  };

  const handleAuthCallback = useCallback(
    async (code: string, state: string) => {
      try {
        setIsLoading(true);
        const redirectUri = window.location.origin;

        const codeVerifier = localStorage.getItem(CODE_VERIFIER_KEY);
        if (!codeVerifier) {
          throw new Error("Code verifier not found");
        }

        const backendAuth = await backendAuthenticate(
          code,
          redirectUri,
          codeVerifier,
        );
        if (!backendAuth) {
          throw new Error("Backend authentication failed");
        }
        localStorage.removeItem(CODE_VERIFIER_KEY);

        const userData: UserInfo = backendAuth;
        setUser(userData);
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));

        try {
          const stateData = JSON.parse(atob(state));
          if (stateData.returnUrl && stateData.returnUrl !== "/") {
            window.history.replaceState(
              {},
              document.title,
              stateData.returnUrl,
            );
          } else {
            window.history.replaceState({}, document.title, "/");
          }
        } catch {
          window.history.replaceState({}, document.title, "/");
        }
      } catch (error) {
        console.error("Error handling auth callback:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const logout = () => {
    setUser(null);
    localStorage.removeItem(USER_DATA_KEY);
    localStorage.removeItem(JWT_TOKEN_KEY);
    localStorage.removeItem(CODE_VERIFIER_KEY);
    queryClient.clear();
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");

    if (code && state) {
      handleAuthCallback(code, state);
      return;
    }

    const storedUserData = localStorage.getItem(USER_DATA_KEY);
    const storedJWT = localStorage.getItem(JWT_TOKEN_KEY);

    if (storedUserData && storedJWT) {
      try {
        const userData = JSON.parse(storedUserData);
        setUser(userData);
      } catch (error) {
        console.error("Error parsing stored user data:", error);
        localStorage.removeItem(USER_DATA_KEY);
        localStorage.removeItem(JWT_TOKEN_KEY);
      }
    }
    setIsLoading(false);
  }, [handleAuthCallback]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
